import { useCallback, useEffect, RefObject } from 'react';
import ScreenService from '../services/screen-service';
import {
  KEYBOARD_KEY_ARROW_LEFT, KEYBOARD_KEY_ARROW_DOWN, KEYBOARD_KEY_ARROW_RIGHT, KEYBOARD_KEY_ARROW_UP,
} from '../constants/system/keyboard-keys';
import { HTML_TAG_NAME_TEXTAREA, HTML_TAG_NAME_INPUT, HTML_TAG_NAME_BUTTON } from '../constants/system/html-tag-names';
import { DATA_REF_NAME } from '../constants/system/data-attributes';
import { SIGN_DOT } from '../constants/system/signs';

const useArrowNavigation: Function = (refList: RefObject<Record<string, HTMLElement>>): void => {
  const changeFocusedTarget: Function = useCallback((target: HTMLElement): void => {
    if (target) {
      const formFocusableTagNameList: string[] = [HTML_TAG_NAME_INPUT, HTML_TAG_NAME_TEXTAREA, HTML_TAG_NAME_BUTTON];

      if (formFocusableTagNameList.includes(target.tagName.toLowerCase())) {
        target.focus();
      } else {
        target.click();
      }
    }
  }, []);

  const getRowIndexList: Function = useCallback((
    refItemList: RefObject<Record<string, HTMLElement>>,
    rowIndex: number,
  ): string[] => (
    Object.keys(refItemList).filter((cellId: string): boolean => cellId.indexOf(`${rowIndex}.`) === 0)
      .sort((itemA: string, itemB: string): number => {
        const fieldIndexA: number = Number(itemA.split(SIGN_DOT)[1]);
        const fieldIndexB: number = Number(itemB.split(SIGN_DOT)[1]);

        return fieldIndexA - fieldIndexB;
      })
  ), []);

  const onKeyDown: ReactEventListener = useCallback((event: React.KeyboardEvent): void => {
    const {
      shiftKey,
      key,
    } = event;

    if (shiftKey && refList) {
      const focusedFormElement: Element | null = ScreenService.getActiveElement() || null;
      const focusedFormElementRefName: string | null = focusedFormElement
        ? focusedFormElement.getAttribute(DATA_REF_NAME) : null;

      if (focusedFormElementRefName) {
        const rowIndex: number = Number(focusedFormElementRefName.split(SIGN_DOT)[0]);
        const cellIndex: number = Number(focusedFormElementRefName.split(SIGN_DOT)[1]);
        const cellSubIndex: number = Number(focusedFormElementRefName.split(SIGN_DOT)[2]);
        const currentCell: string = `${rowIndex}${SIGN_DOT}${cellIndex}`
          + `${cellSubIndex ? `${SIGN_DOT}${cellSubIndex}` : ''}`;

        const currentRowIndexList: string[] = getRowIndexList(refList.current, rowIndex);
        const currentCellIndex: number = currentRowIndexList.indexOf(currentCell);

        const activeKeyMap: Record<string, string> = {
          [KEYBOARD_KEY_ARROW_DOWN]: `${rowIndex + 1}${SIGN_DOT}${cellIndex}`
          + `${cellSubIndex ? `${SIGN_DOT}${cellSubIndex}` : ''}`,
          [KEYBOARD_KEY_ARROW_UP]: `${rowIndex - 1}${SIGN_DOT}${cellIndex}`
          + `${cellSubIndex ? `${SIGN_DOT}${cellSubIndex}` : ''}`,
          [KEYBOARD_KEY_ARROW_RIGHT]: currentRowIndexList[currentCellIndex + 1],
          [KEYBOARD_KEY_ARROW_LEFT]: currentRowIndexList[currentCellIndex - 1],
        };

        if (Object.keys(activeKeyMap).includes(key)) {
          const nextCellKey: string = activeKeyMap[key];

          if (refList.current) {
            changeFocusedTarget(refList.current[nextCellKey]);
          }

          event.preventDefault();
        }
      }
    }
  }, []);

  useEffect((): ReactUseEffectCleanUp => {
    ScreenService.addEventListener('keydown', onKeyDown);

    return (): void => {
      ScreenService.removeEventListener('keydown', onKeyDown);
    };
  }, [onKeyDown]);
};

export default useArrowNavigation;
